import React, { useEffect, useMemo, useState } from "react";
import useAxios from "../../utils/useAxios";
import { useTranslation } from "react-i18next";
import { Container } from "react-bootstrap";
import Skeleton from "react-loading-skeleton";
import FeatherIcon from "feather-icons-react";
import debounce from "lodash.debounce";
import { useHistory, useLocation } from "react-router-dom";
import MenuList from "../../components/MenuList";
import SuperAdminUserDetailModal from "./SuperAdminUserDetailModal";

const SuperAdminUsersList = () => {
    const { t } = useTranslation();
    const api = useAxios();
    const history = useHistory();
    const location = useLocation();

    const [users, setUsers] = useState([]);
    const [loader, setLoader] = useState(false);
    const [showOnlyAdmins, setShowOnlyAdmins] = useState(true);

    const [nextUrl, setNextUrl] = useState(null);
    const [isLoadingMore, setIsLoadingMore] = useState(false);

    const searchUsers = async (queryTerm, onlyAdminsParam) => {
        setLoader(true);
        try {
            const params = new URLSearchParams();
            if (queryTerm && queryTerm.length >= 2) params.append("search", queryTerm);
            if (onlyAdminsParam) params.append("is_admin", "true");

            const res = await api.get(`/api/v1.0/admin-users/?${params.toString()}`);
            setUsers(res.data.results || res.data);

            setNextUrl(res.data.next ? res.data.next.replace(/^.*\/\/[^/]+/, '') : null);
        } catch (e) {
            console.error(e);
        } finally {
            setLoader(false);
        }
    };

    const loadMore = async () => {
        if (!nextUrl || isLoadingMore) return;
        setIsLoadingMore(true);
        try {
            const res = await api.get(nextUrl);
            setUsers(prev => [...prev, ...(res.data.results || [])]);
            setNextUrl(res.data.next ? res.data.next.replace(/^.*\/\/[^/]+/, '') : null);
        } catch (e) {
            console.error(e);
        } finally {
            setIsLoadingMore(false);
        }
    };

    useEffect(() => {
        const handleScroll = () => {
            if (window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 200) {
                loadMore();
            }
        };
        window.addEventListener("scroll", handleScroll);
        return () => window.removeEventListener("scroll", handleScroll);
    }, [nextUrl, isLoadingMore]);

    const debouncedSearch = useMemo(
        () => debounce((val, adm) => searchUsers(val, adm), 400),
        []
    );

    useEffect(() => () => debouncedSearch.cancel(), [debouncedSearch]);

    useEffect(() => {
        document.title = t("Super Admin Dashboard");
        const savedQuery = sessionStorage.getItem("superadmin_search") || "";
        searchUsers(savedQuery, showOnlyAdmins);
    }, [showOnlyAdmins]);

    const handleItemClick = (user) => {
        const params = new URLSearchParams(location.search);
        params.set("admin_id", user.id);
        history.push({ search: params.toString() });
    };

    const reloadData = () => {
        const savedQuery = sessionStorage.getItem("superadmin_search") || "";
        searchUsers(savedQuery, showOnlyAdmins);
    };

    return (
        <Container className="page_container">
            <nav className="tabs-container">
                <ul className="tabs">
                    <li className="easy-black">
                        {t("Super Admin Dashboard")}
                    </li>
                </ul>
            </nav>
            <br />
            <nav className="tabs-container" style={{ paddingBottom: 0, marginTop: "-20px" }}>
                <ul className="tabs">
                    <li style={{ position: "relative", width: "100%", display: "flex", alignItems: "center" }}>
                        <div style={{ position: "relative", flex: 1 }}>
                            <FeatherIcon
                                icon="search"
                                size={16}
                                className="search-icon"
                            />
                            <input
                                className="search"
                                type="text"
                                placeholder={t("Search users by email, first name, last name")}
                                defaultValue={sessionStorage.getItem("superadmin_search") || ""}
                                onKeyUp={e => {
                                    const val = e.target.value;
                                    sessionStorage.setItem("superadmin_search", val);
                                    debouncedSearch(val, showOnlyAdmins);
                                }}
                                style={{ paddingLeft: "30px" }}
                            />
                        </div>
                    </li>
                </ul>
            </nav>
            <label style={{ display: "flex", alignItems: "center", gap: "8px", cursor: "pointer", whiteSpace: "nowrap", paddingRight: "10px", marginTop: 0 }}>
                <input
                    type="checkbox"
                    checked={showOnlyAdmins}
                    onChange={(e) => setShowOnlyAdmins(e.target.checked)}
                />
                {t("Show only admins")}
            </label>
            <div className="admin-wrapper">
                <div className="admin-list" style={{ marginTop: "10px" }}>
                    <div className="admin-header" style={{ display: "grid", gridTemplateColumns: "1.5fr 1.5fr 100px 40px", gap: "10px" }}>
                        <div>{t("Email")}</div>
                        <div>{t("Name")}</div>
                        <div>{t("Is Admin")}</div>
                        <div>{t("Actions")}</div>
                    </div>

                    {loader ? (
                        Array.from({ length: 5 }).map((_, idx) => (
                            <Skeleton key={idx} height={75} baseColor="#fff" highlightColor="#f3f3f3" className="admin-skeleton" />
                        ))
                    ) : (
                        users.map((user) => (
                            <div
                                className="admin-card"
                                key={user.id}
                                onClick={() => handleItemClick(user)}
                                style={{
                                    display: "grid",
                                    gridTemplateColumns: "1.5fr 1.5fr 100px 40px",
                                    gap: "10px",
                                    alignItems: "center"
                                }}
                            >
                                <div style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{user.email}</div>
                                <div><span className="uppercase">{user.last_name || ""}</span><br/>{user.first_name || ""}</div>
                                <div>
                                    {user.is_admin && <FeatherIcon icon="check" />}
                                </div>
                                <div className="actions" onClick={(e) => e.stopPropagation()}>
                                    <MenuList
                                        is_edit
                                        onEdit={() => handleItemClick(user)}
                                        editText={t("Manage")}
                                        is_delete={false}
                                    />
                                </div>
                            </div>
                        ))
                    )}

                    {/* 5. Индикатор загрузки внизу при скролле */}
                    {isLoadingMore && (
                        <div style={{ marginTop: "10px" }}>
                            <Skeleton height={75} baseColor="#fff" highlightColor="#f3f3f3" className="admin-skeleton" />
                        </div>
                    )}

                    {!loader && users.length === 0 && (
                        <div style={{ padding: "40px", textAlign: "center", color: "var(--secondary-color)" }}>
                            {t("No users found")}
                        </div>
                    )}
                </div>
            </div>

            <SuperAdminUserDetailModal onUserUpdate={reloadData} />
        </Container>
    );
};

export default SuperAdminUsersList;
